import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Title } from "react-admin";
import { getJson } from "../../utils/api";
import {
  AdminUserProgressResponse,
  AdminLessonAttemptsResponse,
  AdminLessonProgressItem,
} from "../../models";
import { ADMIN, formatSeconds } from "./utils";

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "--";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const statusChipColor = (
  status: string
): "success" | "info" | "default" | "warning" => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "IN_PROGRESS":
      return "info";
    case "AVAILABLE":
      return "default";
    default:
      return "default";
  }
};

export const UserLearningProgress: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [progress, setProgress] = useState<AdminUserProgressResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null);
  const [lessonAttempts, setLessonAttempts] = useState<
    Record<string, AdminLessonAttemptsResponse>
  >({});
  const [loadingAttempts, setLoadingAttempts] = useState<
    Record<string, boolean>
  >({});
  const [attemptErrors, setAttemptErrors] = useState<Record<string, string>>(
    {}
  );

  // Fix #6: Track mounted state to prevent state updates after unmount
  const mountedRef = useRef(true);

  const fetchProgress = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const resp = await getJson(`${ADMIN}/users/${userId}/learning/progress`);
      const { data } = await resp.json();
      if (mountedRef.current) setProgress(data);
    } catch (e) {
      console.error("Failed to fetch user progress", e);
      if (mountedRef.current) setError("Failed to load learning progress");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [userId]);

  const fetchLessonAttempts = useCallback(
    async (lessonId: string) => {
      if (!userId) return;
      setLoadingAttempts((prev) => ({ ...prev, [lessonId]: true }));
      setAttemptErrors((prev) => ({ ...prev, [lessonId]: "" }));
      try {
        const resp = await getJson(
          `${ADMIN}/users/${userId}/learning/lessons/${lessonId}/attempts`
        );
        const { data } = await resp.json();
        if (mountedRef.current) {
          setLessonAttempts((prev) => ({ ...prev, [lessonId]: data }));
        }
      } catch (e) {
        console.error("Failed to fetch lesson attempts", e);
        if (mountedRef.current) {
          setAttemptErrors((prev) => ({
            ...prev,
            [lessonId]: "Failed to load question details",
          }));
        }
      } finally {
        if (mountedRef.current) {
          setLoadingAttempts((prev) => ({ ...prev, [lessonId]: false }));
        }
      }
    },
    [userId]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchProgress();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchProgress]);

  const handleToggleLesson = (lesson: AdminLessonProgressItem) => {
    if (lesson.status !== "COMPLETED") return;
    if (expandedLessonId === lesson.lesson_id) {
      setExpandedLessonId(null);
    } else {
      setExpandedLessonId(lesson.lesson_id);
      if (!lessonAttempts[lesson.lesson_id]) {
        fetchLessonAttempts(lesson.lesson_id);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error || !progress) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error || "No data found"}</Typography>
      </Box>
    );
  }

  const { summary, streak, lessons } = progress;

  return (
    <Box sx={{ p: 2, maxWidth: 1400 }}>
      <Title title={`Learning - ${userId}`} />

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Learning Progress</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {userId}
        </Typography>
      </Box>

      {/* R2.1 — Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            <SummaryItem
              label="Lessons Completed"
              value={`${summary.completed_count} / ${summary.total_lessons}`}
            />
            <SummaryItem
              label="In Progress"
              value={String(summary.in_progress_count)}
            />
            <SummaryItem
              label="Current Streak"
              value={`${streak.current_streak} days`}
            />
            <SummaryItem
              label="Best Streak"
              value={`${streak.best_streak} days`}
            />
            <SummaryItem
              label="Last Lesson"
              value={streak.last_lesson_date || "--"}
            />
            <SummaryItem
              label="Avg Accuracy"
              value={
                summary.average_accuracy != null
                  ? `${summary.average_accuracy}%`
                  : "--"
              }
            />
            <SummaryItem
              label="Total Time"
              value={formatSeconds(summary.total_time_seconds)}
            />
          </Box>
        </CardContent>
      </Card>

      {/* R2.2 — Lesson Progress Table */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Lesson Progress
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Lesson</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Accuracy</TableCell>
              <TableCell align="right">Time</TableCell>
              <TableCell align="right">Retries</TableCell>
              <TableCell align="right">Completed</TableCell>
              <TableCell align="center">CMS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lessons.map((lesson) => (
              <React.Fragment key={lesson.lesson_id}>
                <TableRow
                  hover
                  onClick={() => handleToggleLesson(lesson)}
                  sx={{
                    cursor:
                      lesson.status === "COMPLETED" ? "pointer" : "default",
                  }}
                >
                  <TableCell sx={{ width: 32, px: 0.5 }}>
                    {lesson.status === "COMPLETED" && (
                      <IconButton size="small">
                        {expandedLessonId === lesson.lesson_id ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{lesson.lesson_title}</TableCell>
                  <TableCell>{lesson.unit_title}</TableCell>
                  <TableCell>
                    <Chip
                      label={lesson.lesson_type}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lesson.status}
                      color={statusChipColor(lesson.status)}
                      size="small"
                      sx={{
                        opacity: lesson.status === "LOCKED" ? 0.5 : 1,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {lesson.first_attempt_accuracy != null
                      ? `${lesson.first_attempt_accuracy}%`
                      : "--"}
                  </TableCell>
                  <TableCell align="right">
                    {formatSeconds(lesson.total_time_seconds)}
                  </TableCell>
                  <TableCell align="right">
                    {lesson.retry_count != null ? lesson.retry_count : "--"}
                  </TableCell>
                  <TableCell align="right">
                    {formatDate(lesson.completed_at)}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MuiLink
                      component={Link}
                      to={`/lessons/${lesson.lesson_id}/edit`}
                      underline="hover"
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </MuiLink>
                  </TableCell>
                </TableRow>

                {/* R2.3 — Expandable Question Detail */}
                {lesson.status === "COMPLETED" && (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      sx={{ py: 0, borderBottom: "none" }}
                    >
                      <Collapse
                        in={expandedLessonId === lesson.lesson_id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ py: 2, px: 2 }}>
                          {loadingAttempts[lesson.lesson_id] ? (
                            <Typography variant="body2">
                              Loading attempts...
                            </Typography>
                          ) : attemptErrors[lesson.lesson_id] ? (
                            <Typography variant="body2" color="error">
                              {attemptErrors[lesson.lesson_id]}
                            </Typography>
                          ) : lessonAttempts[lesson.lesson_id] ? (
                            <QuestionDetails
                              data={lessonAttempts[lesson.lesson_id]}
                            />
                          ) : null}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {lessons.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No lessons found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* --- Helper Components --- */

const SummaryItem: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

const QuestionDetails: React.FC<{ data: AdminLessonAttemptsResponse }> = ({
  data,
}) => (
  <Box>
    {data.nodes.map((node) => (
      <Box key={node.node_id} sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
          {node.question_text}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {node.question_type} &middot; First attempt:{" "}
          {node.first_attempt_correct ? "Correct" : "Incorrect"} &middot;{" "}
          {node.total_attempts} attempt(s) &middot;{" "}
          {(node.total_time_ms / 1000).toFixed(1)}s total
        </Typography>
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Attempt #</TableCell>
              <TableCell>Correct?</TableCell>
              <TableCell align="right">Time</TableCell>
              <TableCell>Selected</TableCell>
              <TableCell>Correct Answer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {node.attempts.map((att, idx) => (
              <TableRow key={idx}>
                <TableCell>{att.attempt_number}</TableCell>
                <TableCell>
                  {att.is_correct ? (
                    <CheckCircleIcon
                      fontSize="small"
                      sx={{ color: "success.main" }}
                    />
                  ) : (
                    <CancelIcon fontSize="small" sx={{ color: "error.main" }} />
                  )}
                </TableCell>
                <TableCell align="right">
                  {(att.time_spent_ms / 1000).toFixed(1)}s
                </TableCell>
                <TableCell>{att.selected_option_text || "--"}</TableCell>
                <TableCell>{att.correct_option_text || "--"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    ))}
    {data.nodes.length === 0 && (
      <Typography variant="body2" color="text.secondary">
        No question attempts found
      </Typography>
    )}
  </Box>
);
